import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import './CustomSelect.css';

/**
 * CustomSelect — Portal-based dropdown.
 * The panel renders into document.body via ReactDOM.createPortal,
 * so it is NEVER clipped by any parent overflow/z-index.
 * It auto-flips upward when there is not enough space below.
 */
export function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  searchable = false,
  className = '',
  lightPanel = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  // Normalise options to [{ value, label, disabled? }]
  const normalised = options.map(o =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  const filtered = searchable
    ? normalised.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : normalised;

  const selectedLabel = normalised.find(o => o.value === value)?.label;

  // ─── Position the portal panel relative to the trigger ──────
  const positionPanel = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const panelHeight = 280;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < panelHeight && spaceAbove > spaceBelow;

    setPanelStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 6 }
        : { top: rect.bottom + 6 }),
    });
  }, []);

  // Reposition on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    positionPanel();
    window.addEventListener('scroll', positionPanel, true);
    window.addEventListener('resize', positionPanel);
    return () => {
      window.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
    };
  }, [open, positionPanel]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Scroll selected item into view when opening
  useEffect(() => {
    if (open && listRef.current && value) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }, [open, value]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open) positionPanel();
    setOpen(prev => !prev);
    setQuery('');
  };

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  // ─── Portal panel ────────────────────────────────────────────
  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className={`csel-panel ${lightPanel ? 'csel-panel-light' : ''}`}
          style={panelStyle}
          role="listbox"
        >
          {searchable && (
            <div className="csel-search-wrapper">
              <Search size={14} className="csel-search-icon" />
              <input
                autoFocus
                type="text"
                className="csel-search-input"
                placeholder="Search..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onMouseDown={e => e.stopPropagation()}
              />
            </div>
          )}
          <ul ref={listRef} className="csel-list">
            {filtered.length === 0 ? (
              <li className="csel-no-results">No results found</li>
            ) : (
              filtered.map(opt => (
                <li
                  key={opt.value}
                  data-selected={opt.value === value}
                  className={`csel-option ${opt.value === value ? 'csel-option-selected' : ''} ${opt.disabled ? 'csel-option-disabled' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!opt.disabled) handleSelect(opt.value);
                  }}
                  role="option"
                  aria-selected={opt.value === value}
                >
                  <span className="csel-option-label">{opt.label}</span>
                  {opt.value === value && <Check size={14} className="csel-check-icon" />}
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`csel-wrapper ${className} ${disabled ? 'csel-disabled' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`csel-trigger ${open ? 'csel-trigger-open' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`csel-trigger-label ${!selectedLabel ? 'csel-placeholder' : ''}`}>
          {selectedLabel || placeholder}
        </span>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {selectedLabel ? (
            <span
              role="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange('');
                setOpen(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px',
                borderRadius: '50%',
                cursor: 'pointer',
                color: '#9ca3af',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
            >
              <X size={14} />
            </span>
          ) : (
            <ChevronDown
              size={16}
              className={`csel-chevron ${open ? 'csel-chevron-open' : ''}`}
            />
          )}
        </div>
      </button>
      {panel}
    </div>
  );
}